/**
 * ProgramContext.js — Sprint 1 Workstream B
 *
 * Tracks which MOSAIC product line the current session is operating in:
 *   - "practice"   → MOSAIC Practice (modality practice)
 *   - "summative"  → MOSAIC Summative (cross-curricular, WIDA®-aligned)
 *
 * Persisted to localStorage so a switch survives a tab reload. Customers
 * who only license one program auto-bypass the chooser screen and land
 * directly inside that program.
 *
 * NOT a license enforcer — that lives in the backend. This is the
 * UX-state layer only.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "mosaic_active_program";

export const PROGRAMS = {
  practice: {
    id: "practice",
    name: "MOSAIC Practice",
    tagline: "Daily modality practice with culturally responsive contexts",
    description:
      "Practice assessments across Listening, Reading, Writing, and Speaking. Reports proficiency on the WIDA® 1–6 scale.",
    accentClass: "text-mosaic-practice",
    bgClass: "bg-mosaic-practice",
    bgLightClass: "bg-mosaic-practice-light",
    borderClass: "border-mosaic-practice",
    icon: "BookOpen",
  },
  summative: {
    id: "summative",
    name: "MOSAIC Summative",
    tagline: "Cross-curricular summative assessment, aligned to WIDA® 2020",
    description:
      "Tiered A/B/C delivery with test-until-ceiling stopping rule. Strict coverage across Language Arts, Math, Science, and Social Studies.",
    accentClass: "text-mosaic-summative",
    bgClass: "bg-mosaic-summative",
    bgLightClass: "bg-mosaic-summative-light",
    borderClass: "border-mosaic-summative",
    icon: "ShieldCheck",
  },
};

const ProgramContext = createContext({
  activeProgram: null,
  switchProgram: () => {},
  clearProgram: () => {},
});

export function ProgramProvider({ children }) {
  const [activeProgram, setActiveProgram] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored && PROGRAMS[stored] ? stored : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (activeProgram) localStorage.setItem(STORAGE_KEY, activeProgram);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, [activeProgram]);

  const switchProgram = useCallback((programId) => {
    if (!PROGRAMS[programId]) return;
    setActiveProgram(programId);
  }, []);

  const clearProgram = useCallback(() => setActiveProgram(null), []);

  const value = useMemo(
    () => ({
      activeProgram,
      program: activeProgram ? PROGRAMS[activeProgram] : null,
      switchProgram,
      clearProgram,
    }),
    [activeProgram, switchProgram, clearProgram]
  );

  return <ProgramContext.Provider value={value}>{children}</ProgramContext.Provider>;
}

export function useProgram() {
  return useContext(ProgramContext);
}
